import { useState } from "react";
import { useHLAuth } from "@/context/HLAuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Loader2, User, Building2, ShieldCheck, Mail, KeyRound } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hl`;

export default function HLAuth({ role }) {
  const { login, register } = useHLAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP state for corporates
  const [otpStep, setOtpStep] = useState("email"); // email → otp → details
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const isAspirant = role === "aspirant";
  const isCorporate = role === "corporate";
  const dashPath = isAspirant ? "/humanloop/aspirant/dashboard" : "/humanloop/corporate/dashboard";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      navigate(data.role === "aspirant" ? "/humanloop/aspirant/dashboard" : "/humanloop/corporate/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  // Aspirant register - simple
  const handleAspirantRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register({ ...form, role: "aspirant" });
      navigate("/humanloop/aspirant/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  // Corporate OTP flow
  const sendOTP = async () => {
    setError("");
    if (!form.email.includes("@")) { setError("Please enter a valid email"); return; }
    setOtpSending(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { email: form.email });
      setOtpStep("otp");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send verification code");
    } finally { setOtpSending(false); }
  };

  const verifyOTP = async () => {
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { email: form.email, otp });
      setOtpVerified(true);
      setOtpStep("details");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const handleCorporateRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register({ ...form, role: "corporate", otp_token: otp });
      navigate("/humanloop/corporate/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });
  const inputClass = "w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all";

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6" data-testid={`hl-auth-${role}`}>
      <div className="w-full max-w-md">
        <Link to="/humanloop" className="text-xs text-slate-500 hover:text-emerald-400 mb-6 inline-block">&larr; Back to The HumanInLoop</Link>

        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
            {isAspirant ? <User className="w-7 h-7 text-emerald-400" /> : <Building2 className="w-7 h-7 text-emerald-400" />}
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isAspirant ? "Professional Portal" : "Employer Portal"}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {isAspirant ? "Create your profile & video pitch" : "Discover talent with real video pitches"}
          </p>
          {isCorporate && <p className="text-[10px] text-emerald-400 mt-2 flex items-center justify-center gap-1"><ShieldCheck size={10} /> Official email with OTP verification required</p>}
        </div>

        {/* Toggle */}
        <div className="flex bg-slate-900 rounded-full p-1 mb-6">
          <button onClick={() => { setMode("login"); setOtpStep("email"); setError(""); }} className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${mode === "login" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`} data-testid="hl-mode-login">Sign In</button>
          <button onClick={() => { setMode("register"); setOtpStep("email"); setError(""); }} className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${mode === "register" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`} data-testid="hl-mode-register">Sign Up</button>
        </div>

        {/* LOGIN */}
        {mode === "login" && (
          <form onSubmit={handleLogin} className="glass-card rounded-2xl p-6 space-y-4" data-testid="hl-auth-form">
            <input type="email" required placeholder="Email" value={form.email} onChange={e => set("email", e.target.value)} className={inputClass} data-testid="hl-input-email" />
            <input type="password" required placeholder="Password" value={form.password} onChange={e => set("password", e.target.value)} className={inputClass} data-testid="hl-input-password" />
            {error && <p className="text-sm text-red-400" data-testid="hl-auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-emerald-500 text-slate-950 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="hl-auth-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Sign In <ArrowRight size={14} /></>}
            </button>
          </form>
        )}

        {/* ASPIRANT REGISTER */}
        {mode === "register" && isAspirant && (
          <form onSubmit={handleAspirantRegister} className="glass-card rounded-2xl p-6 space-y-4" data-testid="hl-auth-form">
            <input type="text" required placeholder="Full Name" value={form.name} onChange={e => set("name", e.target.value)} className={inputClass} data-testid="hl-input-name" />
            <input type="email" required placeholder="Email" value={form.email} onChange={e => set("email", e.target.value)} className={inputClass} data-testid="hl-input-email" />
            <input type="password" required placeholder="Password (min 6 chars)" value={form.password} onChange={e => set("password", e.target.value)} className={inputClass} data-testid="hl-input-password" />
            {error && <p className="text-sm text-red-400" data-testid="hl-auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-emerald-500 text-slate-950 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="hl-auth-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Create Account <ArrowRight size={14} /></>}
            </button>
          </form>
        )}

        {/* CORPORATE REGISTER — Step 1: Email */}
        {mode === "register" && isCorporate && otpStep === "email" && (
          <div className="glass-card rounded-2xl p-6 space-y-4" data-testid="hl-otp-step-email">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-white">Step 1: Verify Your Official Email</p>
                <p className="text-[10px] text-slate-500">Personal emails (Gmail, Yahoo, etc.) are not accepted</p>
              </div>
            </div>
            <input type="email" required placeholder="yourname@company.com" value={form.email} onChange={e => set("email", e.target.value)} className={inputClass} data-testid="hl-input-email" />
            {error && <p className="text-sm text-red-400" data-testid="hl-auth-error">{error}</p>}
            <button onClick={sendOTP} disabled={otpSending || !form.email} className="w-full bg-emerald-500 text-slate-950 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="hl-send-otp">
              {otpSending ? <Loader2 size={16} className="animate-spin" /> : <>Send Verification Code <ArrowRight size={14} /></>}
            </button>
          </div>
        )}

        {/* CORPORATE REGISTER — Step 2: OTP */}
        {mode === "register" && isCorporate && otpStep === "otp" && (
          <div className="glass-card rounded-2xl p-6 space-y-4" data-testid="hl-otp-step-verify">
            <div className="flex items-center gap-3 mb-2">
              <KeyRound className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-white">Step 2: Enter Verification Code</p>
                <p className="text-[10px] text-slate-500">Code sent to {form.email}</p>
              </div>
            </div>
            <input type="text" maxLength={6} placeholder="Enter 6-digit code" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono`} data-testid="hl-input-otp" />
            {error && <p className="text-sm text-red-400" data-testid="hl-auth-error">{error}</p>}
            <button onClick={verifyOTP} disabled={loading || otp.length !== 6} className="w-full bg-emerald-500 text-slate-950 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="hl-verify-otp">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Verify Code <ShieldCheck size={14} /></>}
            </button>
            <button onClick={() => { setOtpStep("email"); setError(""); }} className="w-full text-xs text-slate-500 hover:text-slate-300 transition-colors py-1">Change email or resend code</button>
          </div>
        )}

        {/* CORPORATE REGISTER — Step 3: Details */}
        {mode === "register" && isCorporate && otpStep === "details" && (
          <form onSubmit={handleCorporateRegister} className="glass-card rounded-2xl p-6 space-y-4" data-testid="hl-auth-form">
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-400">{form.email} — verified</span>
            </div>
            <input type="text" required placeholder="Your Full Name" value={form.name} onChange={e => set("name", e.target.value)} className={inputClass} data-testid="hl-input-name" />
            <input type="text" required placeholder="Company Name" value={form.company_name} onChange={e => set("company_name", e.target.value)} className={inputClass} data-testid="hl-input-company" />
            <input type="password" required placeholder="Create Password (min 6 chars)" value={form.password} onChange={e => set("password", e.target.value)} className={inputClass} data-testid="hl-input-password" />
            {error && <p className="text-sm text-red-400" data-testid="hl-auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-emerald-500 text-slate-950 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="hl-auth-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Create Employer Account <ArrowRight size={14} /></>}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-slate-600 mt-6">
          {isAspirant ? "Looking to hire? " : "Are you a professional? "}
          <Link to={isAspirant ? "/humanloop/corporate" : "/humanloop/aspirant"} className="text-emerald-400 hover:text-emerald-300">
            {isAspirant ? "Employer Portal" : "Aspirant Portal"}
          </Link>
        </p>
      </div>
    </div>
  );
}
